import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Stack,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  FamilyRestroom as FamilyIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Timeline,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Format dates for display (MM/DD/YYYY)
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';

  // Parse YYYY-MM-DD HH:mm:ss format
  const [datePart] = dateStr.split(' ');
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Get date range title
const getDateRangeTitle = (startDate, endDate, dateRange, t) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Parse YYYY-MM-DD safely to avoid timezone issues
  const parseYMD = (str) => {
    if (!str) return null;
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const start = parseYMD(startDate);
  const end = parseYMD(endDate);

  // Format date as "MMM d, yyyy" (e.g., "Jan 1, 2024")
  const formatDate = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  };

  // Format month as "MMM yyyy" (e.g., "Jan 2024")
  const formatMonth = (date) => {
    return date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
  };

  // Helper function to compare dates ignoring time
  const isSameDay = (date1, date2) => {
    return date1 && date2 &&
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  switch (dateRange) {
    case 'day':
      if (isSameDay(end, today)) {
        return t('common.today');
      } else if (isSameDay(end, yesterday)) {
        return t('common.yesterday');
      } else {
        return formatDate(end);
      }
    case 'week': {
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - today.getDay());
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

      if (isSameDay(end, thisWeekEnd)) {
        return t('common.thisWeek');
      } else if (isSameDay(end, lastWeekEnd)) {
        return t('common.lastWeek');
      } else {
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
    }
    case 'month': {
      const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

      if (end && end.getFullYear() === thisMonthEnd.getFullYear() && end.getMonth() === thisMonthEnd.getMonth()) {
        return t('common.thisMonth');
      } else if (end && end.getFullYear() === lastMonthEnd.getFullYear() && end.getMonth() === lastMonthEnd.getMonth()) {
        return t('common.lastMonth');
      } else {
        return formatMonth(end);
      }
    }
    default:
      return `${formatDate(start)} - ${formatDate(end)}`;
  }
};

// Stat Card Component
const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 100, sm: 140 },
        bgcolor: 'background.paper',
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          component="h2"
          variant="h6"
          gutterBottom
          sx={{
            color: color,
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }}
        >
          {title}
        </Typography>
        <Box sx={{
          color: color,
          '& svg': {
            fontSize: { xs: '1.8rem', sm: '1.5rem' }
          }
        }}>
          {icon}
        </Box>
      </Box>
      <Typography
        component="p"
        variant="h4"
        color="text.primary"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.5rem' }
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
};

const IndividualNumbers = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  // Get first and last day of the current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';

    // Handle both Date objects and UTC Date objects
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    // Return in YYYY-MM-DD format for the date picker
    return `${year}-${month}-${day}`;
  };

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('submitDate');
  const [order, setOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('month');
  const [filters, setFilters] = useState({
    startDate: formatDateForInput(firstDayOfMonth),
    endDate: formatDateForInput(lastDayOfMonth),
    dateType: 'submit',
    carrier: [],
    product: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    products: [],
  });

  // Fetch individual user stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');

        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get('/api/individual/stats', {
          headers: { 'x-auth-token': token },
          params: filters
        });

        setStats(response.data);

        // Extract unique values for filters
        if (response.data.closes) {
          const carriers = [...new Set(response.data.closes.map(close => close.carrier))].filter(Boolean);
          const products = [...new Set(response.data.closes.map(close => close.product))].filter(Boolean);

          setFilterOptions({
            carriers,
            products,
          });
        }

        setLoading(false);
      } catch (err) {
        setError(t('individualNumbers.failedToLoad'));
        setLoading(false);
      }
    };

    fetchStats();
  }, [filters, navigate]);

  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    let startDate, endDate;
    const today = new Date();

    switch (range) {
      case 'day':
        // Set to the current date at midnight UTC
        startDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0));
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999));
        break;
      case 'week':
        // Start of week (Sunday) at midnight UTC
        startDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay(), 0, 0, 0));
        // End of week (Saturday) at end of day UTC
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 6, 23, 59, 59, 999));
        break;
      case 'month':
        // Start of month at midnight UTC
        startDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0));
        // End of month at end of day UTC
        endDate = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999));
        break;
      default:
        return;
    }

    setDateRange(range);
    setFilters(prev => ({
      ...prev,
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    }));
  };

  // Handle date shifting (next/previous day, week, month)
  const handleDateShift = (direction) => {
    const currentStartDate = new Date(filters.startDate);
    const currentEndDate = new Date(filters.endDate);
    let newStartDate, newEndDate;

    switch (dateRange) {
      case 'day':
        newStartDate = new Date(currentStartDate);
        newEndDate = new Date(currentEndDate);
        newStartDate.setDate(currentStartDate.getDate() + (direction === 'back' ? -1 : 1));
        newEndDate.setDate(currentEndDate.getDate() + (direction === 'back' ? -1 : 1));
        break;
      case 'week':
        newStartDate = new Date(currentStartDate);
        newEndDate = new Date(currentEndDate);
        newStartDate.setDate(currentStartDate.getDate() + (direction === 'back' ? -7 : 7));
        newEndDate.setDate(currentEndDate.getDate() + (direction === 'back' ? -7 : 7));
        break;
      case 'month':
        newStartDate = new Date(currentStartDate);
        newStartDate.setMonth(currentStartDate.getMonth() + (direction === 'back' ? -1 : 1));
        newEndDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth() + 1, 0);
        break;
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      startDate: formatDateForInput(newStartDate),
      endDate: formatDateForInput(newEndDate)
    }));
  };

  // Handle sort changes
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort function for close entries
  const sortCloses = (data) => {
    if (!data) return [];

    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Convert date strings to Date objects for comparison
      if (orderBy === 'submitDate' || orderBy === 'effectiveDate') {
        // Parse YYYY-MM-DD HH:mm:ss format
        if (aValue) {
          const [datePart] = aValue.split(' ');
          if (datePart) {
            const [year, month, day] = datePart.split('-');
            aValue = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
          } else {
            aValue = new Date(0);
          }
        } else {
          aValue = new Date(0);
        }

        if (bValue) {
          const [datePart] = bValue.split(' ');
          if (datePart) {
            const [year, month, day] = datePart.split('-');
            bValue = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
          } else {
            bValue = new Date(0);
          }
        } else {
          bValue = new Date(0);
        }
      }

      // Convert AP to numbers
      if (orderBy === 'ap') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (order === 'desc') {
        return bValue > aValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setFilters({
      startDate: formatDateForInput(firstDayOfMonth),
      endDate: formatDateForInput(lastDayOfMonth),
      dateType: 'submit',
      carrier: [],
      product: [],
    });
    setDateRange('month');
  };

  // Filter closes by search query
  const getFilteredCloses = () => {
    if (!stats || !stats.closes) return [];

    const sortedCloses = sortCloses(stats.closes);

    if (!searchQuery) return sortedCloses;

    const query = searchQuery.toLowerCase();
    const filtered = sortedCloses.filter(close => {
      const matchesSearch = close.carrier?.toLowerCase().includes(query) ||
                            close.product?.toLowerCase().includes(query) ||
                            close.status?.toLowerCase().includes(query) ||
                            close.insured_name?.toLowerCase().includes(query);

      const matchesCarrier = filters.carrier.length === 0 ||
                             filters.carrier.includes(close.carrier);

      const matchesProduct = filters.product.length === 0 ||
                             filters.product.includes(close.product);

      // Improved date comparison with UTC dates to avoid timezone issues
      let matchesDate = true;
      if (filters.startDate || filters.endDate) {
        if (!close.submitDate) {
          matchesDate = false;
        } else {
          // Parse YYYY-MM-DD HH:mm:ss format
          const [datePart] = close.submitDate.split(' ');
          if (!datePart) {
            matchesDate = false;
          } else {
            const [year, month, day] = datePart.split('-');
            // Create date at UTC midnight to avoid timezone issues
            const closeDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));

            if (filters.startDate) {
              const [sYear, sMonth, sDay] = filters.startDate.split('-');
              const startDate = new Date(Date.UTC(sYear, sMonth - 1, sDay, 0, 0, 0));
              matchesDate = matchesDate && closeDate >= startDate;
            }

            if (filters.endDate) {
              const [eYear, eMonth, eDay] = filters.endDate.split('-');
              const endDate = new Date(Date.UTC(eYear, eMonth - 1, eDay, 23, 59, 59, 999));
              matchesDate = matchesDate && closeDate <= endDate;
            }
          }
        }
      }

      return matchesSearch && matchesCarrier && matchesProduct && matchesDate;
    });

    return sortCloses(filtered);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{
        mb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pt: { xs: 3, sm: 1 },
      }}>
        <Typography variant="h4" gutterBottom>
          {t('individualNumbers.title')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ mr: 2, fontWeight: 'medium' }}>
            {getDateRangeTitle(filters.startDate, filters.endDate, dateRange, t)}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleDateShift('back')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <ToggleButtonGroup
              value={dateRange}
              exclusive
              onChange={(e, value) => value && handleDateRangeChange(value)}
              size="small"
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="day">{t('common.day')}</ToggleButton>
              <ToggleButton value="week">{t('common.week')}</ToggleButton>
              <ToggleButton value="month">{t('common.month')}</ToggleButton>
            </ToggleButtonGroup>

            <IconButton
              size="small"
              onClick={() => handleDateShift('forward')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}

      {/* Filter controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.dateType')}</InputLabel>
              <Select
                name="dateType"
                value={filters.dateType}
                label={t('common.dateType')}
                onChange={handleFilterChange}
              >
                <MenuItem value="submit">{t('common.submitDate')}</MenuItem>
                <MenuItem value="effective">{t('common.effectiveDate')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.carrier')}</InputLabel>
              <Select
                name="carrier"
                value={filters.carrier}
                label={t('common.carrier')}
                onChange={handleFilterChange}
                multiple
                renderValue={(selected) =>
                  selected.length === 0
                    ? t('common.allCarriers')
                    : selected.join(', ')
                }
              >
                {filterOptions.carriers.map(carrier => (
                  <MenuItem key={carrier} value={carrier}>
                    {carrier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.product')}</InputLabel>
              <Select
                name="product"
                value={filters.product}
                label={t('common.product')}
                onChange={handleFilterChange}
                multiple
                renderValue={(selected) =>
                  selected.length === 0
                    ? t('common.allProducts')
                    : selected.join(', ')
                }
              >
                {filterOptions.products.map(product => (
                  <MenuItem key={product} value={product}>
                    {product}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth
              sx={{
                height: '40px',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              {t('common.clearFilters')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Stats */}
      {stats && (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={t('individualNumbers.production')}
              value={formatCurrency(stats.totalAP || 0)}
              icon={<TrendingUpIcon />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={t('individualNumbers.averageAP')}
              value={formatCurrency(stats.totalCloses ? (stats.totalAP / stats.totalCloses) : 0)}
              icon={<MoneyIcon />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={t('individualNumbers.familiesProtected')}
              value={stats.totalCloses || 0}
              icon={<FamilyIcon />}
              color="#ed6c02"
            />
          </Grid>
        </Grid>
      )}

      {/* Carrier and Product Breakdowns */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('individualNumbers.carrierBreakdown')}
              </Typography>
              <TableContainer sx={{
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 400, // Smaller minimum width for these tables
                },
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap'
                }
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.carrier')}</TableCell>
                      <TableCell align="right">{t('common.policies')}</TableCell>
                      <TableCell align="right">{t('common.ap')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.carrierStats?.map((carrier) => (
                      <TableRow key={carrier.carrier}>
                        <TableCell>{carrier.carrier}</TableCell>
                        <TableCell align="right">{carrier.closes}</TableCell>
                        <TableCell align="right">{formatCurrency(carrier.totalAP)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('individualNumbers.productBreakdown')}
              </Typography>
              <TableContainer sx={{
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 400, // Smaller minimum width for these tables
                },
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap'
                }
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.product')}</TableCell>
                      <TableCell align="right">{t('common.policies')}</TableCell>
                      <TableCell align="right">{t('common.ap')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.productStats?.map((product) => (
                      <TableRow key={product.product}>
                        <TableCell>{product.product}</TableCell>
                        <TableCell align="right">{product.closes}</TableCell>
                        <TableCell align="right">{formatCurrency(product.totalAP)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Policy List */}
      <Typography variant="h5" gutterBottom color="text.primary" sx={{ mt: 4, mb: 2 }}>
        {t('individualNumbers.myPolicies')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('individualNumbers.searchPolicies')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: 650, // Ensures table has minimum width
          },
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap', // Prevents text wrapping in cells
          }
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'carrier'}
                  direction={orderBy === 'carrier' ? order : 'asc'}
                  onClick={() => handleSort('carrier')}
                >
                  {t('common.carrier')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'product'}
                  direction={orderBy === 'product' ? order : 'asc'}
                  onClick={() => handleSort('product')}
                >
                  {t('common.product')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'submitDate'}
                  direction={orderBy === 'submitDate' ? order : 'asc'}
                  onClick={() => handleSort('submitDate')}
                >
                  {t('common.submitDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'effectiveDate'}
                  direction={orderBy === 'effectiveDate' ? order : 'asc'}
                  onClick={() => handleSort('effectiveDate')}
                >
                  {t('common.effectiveDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'ap'}
                  direction={orderBy === 'ap' ? order : 'asc'}
                  onClick={() => handleSort('ap')}
                >
                  {t('common.ap')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  {t('agentLifecycle.status')}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredCloses().map((close, index) => (
              <TableRow key={index}>
                <TableCell>{close.carrier}</TableCell>
                <TableCell>{close.product}</TableCell>
                <TableCell>{formatDateForDisplay(close.submitDate)}</TableCell>
                <TableCell>{formatDateForDisplay(close.effectiveDate)}</TableCell>
                <TableCell align="right">{formatCurrency(close.ap)}</TableCell>
                <TableCell>
                  <Chip
                    label={close.status}
                    size="small"
                    color={close.status === 'Issued' ? 'success' : close.status === 'Pending' ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
            {getFilteredCloses().length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">{t('individualNumbers.noPoliciesFound')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default IndividualNumbers;
